import {
  useEffect,
  useRef,
  useState,
  type ChangeEvent,
  type PointerEvent as ReactPointerEvent,
  type ReactNode,
} from 'react';
import {
  SIG_FONTS,
  getSignatureProfile,
  saveSignatureProfile,
  hasSavedSignature,
  typedToDataUrl,
  type SignatureCandidate,
} from '../lib/signature.js';
import { api } from '../lib/api.js';

/** credifyfbs#116: how this user signs (server-side; read by the Form Builder's Provider signature). */
interface SignatureCredentials {
  signedName: string;
  title: string;
  licenseNumber: string;
  defaultName?: string;
}

const fmtToday = () =>
  new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

// Pen/edit glyph shown before the title (mockup #sigEditorTitle SVG).
const PEN_ICON = (
  <svg
    width="15"
    height="15"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    style={{ verticalAlign: -2 }}
    aria-hidden
  >
    <path d="M12 19l7-7 3 3-7 7-3-3z" />
    <path d="M18 13l-1.5-7.5L2 2l3.5 14.5L13 18l5-5z" />
    <path d="M2 2l7.586 7.586" />
    <circle cx="11" cy="11" r="2" />
  </svg>
);

/**
 * Signature modal shell — reproduces the Forms Hub mockup's chrome (bordered
 * `.modal-head` with pen icon + circular ✕, `.modal-body`, right-aligned
 * `.modal-foot`) rather than the generic app Modal. ESC + backdrop-click close.
 */
function SigModal({
  title,
  onClose,
  actions,
  children,
  className,
}: {
  title: string;
  onClose: () => void;
  actions: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    document.addEventListener('keydown', onKey);
    ref.current?.focus();
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);
  return (
    <div className="modal-backdrop" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div
        className={`modal sig-modal${className ? ` ${className}` : ''}`}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        tabIndex={-1}
        ref={ref}
      >
        <div className="sig-modal-head">
          <h2>
            {PEN_ICON} {title}
          </h2>
          <button type="button" className="sig-modal-close" aria-label="Close" onClick={onClose}>
            ✕
          </button>
        </div>
        <div className="sig-modal-body">{children}</div>
        <div className="sig-modal-foot">{actions}</div>
      </div>
    </div>
  );
}

/** Minimal task shape the signature flow needs for its context copy. */
export interface SignTarget {
  form: string | null;
  patient: string | null;
}

/**
 * Signature editor (mockup #sigEditorModal). Three capture modes — Type (with a
 * font grid), Upload (PNG/JPG ≤500KB) and Draw (canvas) — plus a signing
 * preference. Three contexts drive the copy + terminal action:
 *   settings — manage your saved signature / preference (self-saves, then closes)
 *   sign     — build a fresh signature for one sign-off (each-time mode)
 *   create   — auto mode with no saved signature yet: build + save, then confirm
 */
export function SignatureEditor({
  userKey,
  context,
  task,
  onClose,
  onToast,
  onSign,
  onCreate,
  signExtra,
}: {
  userKey: string;
  context: 'settings' | 'sign' | 'create';
  task?: SignTarget | null;
  onClose: () => void;
  onToast: (msg: string) => void;
  /** sign context: the fresh signature is ready — commit the sign-off. */
  onSign?: () => void;
  /** create context: signature saved as auto — proceed to the confirmation. */
  onCreate?: () => void;
  /** Extra controls (e.g. co-sign) rendered in the sign context, above the footer. */
  signExtra?: ReactNode;
}) {
  const prof = useRef(getSignatureProfile(userKey)).current;
  const [tab, setTab] = useState<'type' | 'upload' | 'draw'>('type');
  const [typedText, setTypedText] = useState(prof.typedText || '');
  const [typedFont, setTypedFont] = useState(prof.typedFont || SIG_FONTS[0].id);
  const [uploadUrl, setUploadUrl] = useState<string | null>(null);
  const [uploadError, setUploadError] = useState('');
  const [drawnDirty, setDrawnDirty] = useState(false);
  const [mode, setMode] = useState<'each_time' | 'auto'>(
    prof.mode === 'auto' ? 'auto' : 'each_time',
  );
  const [saveAndAuto, setSaveAndAuto] = useState(false);
  const [saving, setSaving] = useState(false);
  // credifyfbs#116: name / title / license shown with this user's signature on forms.
  const [creds, setCreds] = useState<SignatureCredentials>({
    signedName: '',
    title: '',
    licenseNumber: '',
  });
  const [credsLoaded, setCredsLoaded] = useState(false);
  const [credsDirty, setCredsDirty] = useState(false);
  useEffect(() => {
    if (context !== 'settings') return;
    let live = true;
    api
      .get<SignatureCredentials>('/me/signature-credentials')
      .then((c) => {
        if (live)
          setCreds({
            signedName: c.signedName ?? '',
            title: c.title ?? '',
            licenseNumber: c.licenseNumber ?? '',
            defaultName: c.defaultName ?? '',
          });
      })
      .catch(() => {})
      .finally(() => {
        if (live) setCredsLoaded(true);
      });
    return () => {
      live = false;
    };
  }, [context]);
  const setCred = (k: 'signedName' | 'title' | 'licenseNumber', v: string) => {
    setCreds((c) => ({ ...c, [k]: v }));
    setCredsDirty(true);
  };
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const drawing = useRef(false);

  const hasInput =
    tab === 'type' ? !!typedText.trim() : tab === 'upload' ? !!uploadUrl : drawnDirty;
  const canSubmit = hasInput || context === 'settings';

  function buildCandidate(): SignatureCandidate | null {
    if (!hasInput) return null;
    if (tab === 'type') {
      const f = SIG_FONTS.find((x) => x.id === typedFont) ?? SIG_FONTS[0];
      return {
        method: 'typed',
        typedText: typedText.trim(),
        typedFont: f.id,
        dataUrl: typedToDataUrl(typedText.trim(), f.css),
      };
    }
    if (tab === 'upload')
      return { method: 'uploaded', typedText: '', typedFont: '', dataUrl: uploadUrl! };
    return {
      method: 'drawn',
      typedText: '',
      typedFont: '',
      dataUrl: canvasRef.current!.toDataURL('image/png'),
    };
  }

  function onFile(e: ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0];
    setUploadError('');
    if (!f) return;
    if (!/^image\/(png|jpeg)$/.test(f.type)) {
      setUploadError('Please choose a PNG or JPG image.');
      return;
    }
    if (f.size > 500 * 1024) {
      setUploadError('Image is too large (max 500KB).');
      return;
    }
    const rd = new FileReader();
    rd.onload = () => setUploadUrl(rd.result as string);
    rd.readAsDataURL(f);
  }

  function clearUpload() {
    if (fileRef.current) fileRef.current.value = '';
    setUploadUrl(null);
  }

  // Canvas drawing (mockup initSigDrawCanvas) — pointer coords mapped to the
  // canvas' intrinsic 840×240 space (credifyfbs#185: a bigger box to sign in; the same
  // 3.5:1 shape as before, drawn at a higher resolution so it stays sharp).
  const stroke =
    (fn: (ctx: CanvasRenderingContext2D, x: number, y: number) => void) =>
    (e: ReactPointerEvent<HTMLCanvasElement>) => {
      const cv = canvasRef.current;
      if (!cv) return;
      const ctx = cv.getContext('2d');
      if (!ctx) return;
      const r = cv.getBoundingClientRect();
      fn(
        ctx,
        (e.clientX - r.left) * (cv.width / r.width),
        (e.clientY - r.top) * (cv.height / r.height),
      );
    };
  const onDrawDown = stroke((ctx, x, y) => {
    ctx.lineWidth = 3.2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.strokeStyle = '#1c3faa';
    drawing.current = true;
    ctx.beginPath();
    ctx.moveTo(x, y);
    ctx.lineTo(x + 0.1, y + 0.1);
    ctx.stroke();
    setDrawnDirty(true);
  });
  const onDrawMove = stroke((ctx, x, y) => {
    if (!drawing.current) return;
    ctx.lineTo(x, y);
    ctx.stroke();
  });
  const stopDraw = () => (drawing.current = false);
  function clearCanvas() {
    const cv = canvasRef.current;
    cv?.getContext('2d')?.clearRect(0, 0, cv.width, cv.height);
    setDrawnDirty(false);
  }

  function onPrimary() {
    const cand = buildCandidate();
    if (context === 'settings') {
      // Preference can be saved on its own; signature bytes change only when new
      // input exists (otherwise the existing saved signature is preserved).
      // ehr#278: the save goes to the server too (the Provider Profile and signed notes
      // show it); the editor stays open when that fails, so Save can try again.
      const sigSaved = saveSignatureProfile(
        userKey,
        cand
          ? { ...cand, mode }
          : {
              method: prof.method,
              dataUrl: prof.dataUrl,
              typedText: prof.typedText,
              typedFont: prof.typedFont,
              mode,
            },
      );
      const done = cand
        ? 'Signature saved. It shows on your profile and on what you sign.'
        : credsDirty
          ? 'Signature details saved.'
          : 'Signing preference saved.';
      const credsSaved = credsDirty
        ? api
            .put<SignatureCredentials>('/me/signature-credentials', {
              signedName: creds.signedName.trim(),
              title: creds.title.trim(),
              licenseNumber: creds.licenseNumber.trim(),
            })
            .then(
              () => true,
              () => false,
            )
        : Promise.resolve(true);
      setSaving(true);
      void Promise.all([sigSaved, credsSaved]).then(([sigOk, credsOk]) => {
        setSaving(false);
        if (!credsOk) onToast('Couldn’t save your signature details. Please try again.');
        else if (!sigOk)
          onToast('Couldn’t reach the server, so your signature was not saved. Please try again.');
        else {
          onToast(done);
          onClose();
        }
      });
      return;
    }
    if (!cand) return;
    if (context === 'create') {
      void saveSignatureProfile(userKey, { ...cand, mode: 'auto' });
      onCreate?.();
    } else {
      // sign
      if (saveAndAuto) void saveSignatureProfile(userKey, { ...cand, mode: 'auto' });
      onSign?.();
    }
  }

  const primaryLabel =
    context === 'settings'
      ? 'Save Signature'
      : context === 'create'
        ? 'Save & Continue'
        : 'Sign & Complete';

  const fontSample = typedText.trim() || 'Your Name';

  return (
    <SigModal
      title="My Signature"
      onClose={onClose}
      actions={
        <>
          <button className="btn" onClick={onClose}>
            Cancel
          </button>
          <button className="btn primary" onClick={onPrimary} disabled={!canSubmit || saving}>
            {primaryLabel}
          </button>
        </>
      }
    >
      {context === 'settings' && hasSavedSignature(prof) && (
        <div className="sig-context-note">
          Current signature ({prof.method}). Build a new one below to replace it.
          <div className="sig-preview-box" style={{ marginTop: 8 }}>
            <img src={prof.dataUrl!} alt="Current signature" />
          </div>
        </div>
      )}
      {context === 'create' && (
        <div className="sig-context-note">
          Auto-sign is on but you have not saved a signature yet. Create one below: it will be saved
          and applied to this sign-off.
        </div>
      )}
      {context === 'sign' && task && (
        <div className="sig-context-note">
          Signing {task.form} for {task.patient}.
        </div>
      )}

      <div className="sig-tabs">
        {(['type', 'upload', 'draw'] as const).map((t) => (
          <button
            key={t}
            type="button"
            className={`sig-tab ${tab === t ? 'active' : ''}`}
            onClick={() => setTab(t)}
          >
            {t === 'type' ? 'Type' : t === 'upload' ? 'Upload' : 'Draw'}
          </button>
        ))}
      </div>

      {tab === 'type' && (
        <div className="sig-pane active">
          <input
            type="text"
            className="sig-typed-input"
            placeholder="Type your full name"
            maxLength={60}
            autoComplete="off"
            value={typedText}
            onChange={(e) => setTypedText(e.target.value)}
          />
          <div className="sig-font-grid">
            {SIG_FONTS.map((f) => (
              <button
                key={f.id}
                type="button"
                className={`sig-font-opt ${f.id === typedFont ? 'active' : ''}`}
                onClick={() => setTypedFont(f.id)}
              >
                <div className="sig-font-name">{f.label}</div>
                <div className="sig-font-sample" style={{ fontFamily: f.css }}>
                  {fontSample}
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      {tab === 'upload' && (
        <div className="sig-pane active">
          <input
            ref={fileRef}
            type="file"
            accept="image/png,image/jpeg"
            style={{ display: 'none' }}
            onChange={onFile}
          />
          <button className="btn" type="button" onClick={() => fileRef.current?.click()}>
            {uploadUrl ? 'Replace image…' : 'Choose image…'}
          </button>
          {uploadError && <span className="sig-error">{uploadError}</span>}
          <div
            className="sig-preview-box"
            role="button"
            tabIndex={0}
            onClick={() => fileRef.current?.click()}
          >
            {uploadUrl ? (
              <>
                <img src={uploadUrl} alt="Uploaded signature" />
                <button
                  type="button"
                  className="sig-upload-remove"
                  title="Remove image"
                  aria-label="Remove image"
                  onClick={(e) => {
                    e.stopPropagation();
                    clearUpload();
                  }}
                >
                  ✕
                </button>
              </>
            ) : (
              <span className="sig-preview-empty">
                Click here to select an image. PNG or JPG, up to 500KB.
              </span>
            )}
          </div>
        </div>
      )}

      {tab === 'draw' && (
        <div className="sig-pane active">
          <canvas
            ref={canvasRef}
            id="sigDrawCanvas"
            width={840}
            height={240}
            onPointerDown={(e) => {
              canvasRef.current?.setPointerCapture(e.pointerId);
              onDrawDown(e);
            }}
            onPointerMove={onDrawMove}
            onPointerUp={stopDraw}
            onPointerCancel={stopDraw}
          />
          <button className="btn sm" type="button" onClick={clearCanvas}>
            Clear
          </button>
        </div>
      )}

      {context === 'settings' && (
        <div>
          <div className="wf-section-title" style={{ marginTop: 16 }}>
            Signature details
          </div>
          <div className="sig-context-note" style={{ marginBottom: 8 }}>
            Shown with your signature on forms (Form Builder provider signatures).
          </div>
          <div className="field-group">
            <label htmlFor="sig-cred-name">Name as signed</label>
            <input
              id="sig-cred-name"
              type="text"
              maxLength={120}
              autoComplete="off"
              placeholder={creds.defaultName || 'Your full name'}
              value={creds.signedName}
              disabled={!credsLoaded}
              onChange={(e) => setCred('signedName', e.target.value)}
            />
          </div>
          <div className="field-row">
            <div className="field-group">
              <label htmlFor="sig-cred-title">Title / credential</label>
              <input
                id="sig-cred-title"
                type="text"
                maxLength={60}
                autoComplete="off"
                placeholder="e.g. LMFT"
                value={creds.title}
                disabled={!credsLoaded}
                onChange={(e) => setCred('title', e.target.value)}
              />
            </div>
            <div className="field-group">
              <label htmlFor="sig-cred-license">License #</label>
              <input
                id="sig-cred-license"
                type="text"
                maxLength={60}
                autoComplete="off"
                placeholder="e.g. LMFT 123456"
                value={creds.licenseNumber}
                disabled={!credsLoaded}
                onChange={(e) => setCred('licenseNumber', e.target.value)}
              />
            </div>
          </div>
          <div className="wf-section-title" style={{ marginTop: 16 }}>
            Signing Preference
          </div>
          <label className="signoff-option" onClick={() => setMode('each_time')}>
            <input type="radio" name="sigMode" checked={mode === 'each_time'} readOnly />
            <div className="signoff-option-body">
              <div className="signoff-option-label">Choose each time</div>
              <div className="signoff-option-desc">
                Sign-offs open with your saved signature filled in. Use a different signature to
                sign another way, just that once.
              </div>
            </div>
          </label>
          <label className="signoff-option" onClick={() => setMode('auto')}>
            <input type="radio" name="sigMode" checked={mode === 'auto'} readOnly />
            <div className="signoff-option-body">
              <div className="signoff-option-label">Auto-apply my saved signature</div>
              <div className="signoff-option-desc">
                Sign-offs show a confirmation with your saved signature already applied.
              </div>
            </div>
          </label>
        </div>
      )}

      {context === 'sign' && (
        <>
          <label className="sig-save-row">
            <input
              type="checkbox"
              checked={saveAndAuto}
              onChange={(e) => setSaveAndAuto(e.target.checked)}
            />{' '}
            Save this signature and auto-apply next time
          </label>
          {signExtra}
        </>
      )}
    </SigModal>
  );
}

/**
 * Auto-apply attestation (mockup #sigConfirmModal). Shows the saved signature
 * already applied; the signer confirms or falls back to a fresh signature.
 */
export function SignConfirmModal({
  userKey,
  task,
  signerLabel,
  submitted,
  due,
  onClose,
  onUseDifferent,
  onSign,
  busy,
  extra,
}: {
  userKey: string;
  task: SignTarget;
  signerLabel: string;
  submitted?: string;
  due?: string;
  onClose: () => void;
  onUseDifferent: () => void;
  onSign: () => void;
  busy?: boolean;
  /** Extra controls (e.g. co-sign) rendered above the footer. */
  extra?: ReactNode;
}) {
  const prof = getSignatureProfile(userKey);
  return (
    <SigModal
      title="Sign & Complete"
      className="sig-confirm-modal"
      onClose={onClose}
      actions={
        <>
          <button className="btn" onClick={onUseDifferent} disabled={busy}>
            Use a different signature
          </button>
          <button className="btn primary" onClick={onSign} disabled={busy}>
            {busy ? 'Signing…' : 'Sign & Complete'}
          </button>
        </>
      }
    >
      <div className="sig-confirm-detail">
        You are signing <strong>{task.form}</strong> for <strong>{task.patient}</strong>.
        {submitted ? ` Submitted ${submitted}.` : ''}
        {due ? ` Due ${due}.` : ''}
      </div>
      <div className="sig-preview-box lg">
        {prof.dataUrl && <img src={prof.dataUrl} alt="Saved signature" />}
      </div>
      <div className="sig-confirm-meta">
        Signer: {signerLabel} · Date: {fmtToday()} · Applying your saved {prof.method} signature
      </div>
      {extra}
    </SigModal>
  );
}
